import { useId, useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Panel, PanelHeader, PanelBody, fieldClass, primaryButtonClass, secondaryButtonClass,
} from "@/components/panel/Panel";
import { REPORT_CATEGORIES, REPORT_NOTE_MAX } from "@/lib/report";
import { makeT } from "@/lib/i18n";

// The "Bildir" dialog for one of Luna's messages (lib/report.js): a reason
// and an optional note, handed to onSubmit({ category, note }) — pages/
// Luna.jsx finds the message's server id and sends it (lib/api.js
// reportMessage). The panels' own shell (components/panel/Panel.jsx):
// Escape and the overlay close it, and focus goes back to the "Bildir"
// button. Native radios in a fieldset, so the arrow keys pick a reason and
// a screen reader reads the group with its question.
export default function ReportDialog({ lang, onSubmit, onClose }) {
  const t = makeT(lang);
  const titleId = useId();
  const radioName = useId();
  const noteId = useId();
  const countId = useId();
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // A report already on its way can't be "cancelled" (Escape, overlay, X).
  const close = () => {
    if (!busy) onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!category || busy) return;
    setBusy(true);
    try {
      await onSubmit({ category, note: note.trim() });
      toast.success(t("Teşekkürler, inceleyeceğiz", "Thanks, we'll review it"));
      onClose();
    } catch {
      setBusy(false);
      toast.error(t("Bildirim gönderilemedi. Biraz sonra tekrar dener misin?",
        "Couldn't send the report. Could you try again in a moment?"));
    }
  };

  return (
    <Panel onClose={close} size="sm" accent="rose" labelledBy={titleId} testId="report-dialog">
      <PanelHeader
        icon={Flag}
        accent="rose"
        title={t("Mesajı bildir", "Report message")}
        subtitle={t("Luna'nın bu cevabında sorun ne? Ekibimiz inceler.", "What's wrong with this reply from Luna? Our team will review it.")}
        titleId={titleId}
        onClose={close}
        closeLabel={t("Kapat", "Close")}
        closeTestId="report-dialog-close"
      />
      <PanelBody>
        <form onSubmit={submit} className="space-y-5">
          <fieldset disabled={busy}>
            <legend className="mb-2.5 text-xs font-semibold text-white/45">{t("Neden bildiriyorsun?", "Why are you reporting it?")}</legend>
            <div className="grid grid-cols-2 gap-2">
              {REPORT_CATEGORIES.map((c) => {
                const active = category === c.key;
                return (
                  <label key={c.key}
                    className={`flex items-center gap-2.5 rounded-xl border min-h-[44px] px-3 text-sm cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-rose-300/60 ${
                      active
                        ? "border-rose-300/40 bg-rose-400/[0.12] text-white"
                        : "border-white/[0.08] bg-white/[0.02] text-white/75 hover:bg-white/[0.06] hover:text-white"
                    }`}>
                    <input type="radio" name={radioName} value={c.key} checked={active}
                      onChange={() => setCategory(c.key)} data-testid={`report-category-${c.key}`}
                      className="accent-rose-400 h-4 w-4 shrink-0 cursor-pointer" />
                    <span>{t(c.tr, c.en)}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label htmlFor={noteId} className="text-xs font-semibold text-white/45">{t("Not (isteğe bağlı)", "Note (optional)")}</label>
              <span id={countId} className="text-[11px] tabular-nums text-white/35">{note.length}/{REPORT_NOTE_MAX}</span>
            </div>
            <textarea id={noteId} value={note} maxLength={REPORT_NOTE_MAX} rows={3} disabled={busy}
              onChange={(e) => setNote(e.target.value.slice(0, REPORT_NOTE_MAX))}
              aria-describedby={countId} data-testid="report-note"
              placeholder={t("İstersen ne olduğunu kısaca yaz.", "If you like, tell us briefly what happened.")}
              className={`${fieldClass} resize-none`} />
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button type="button" onClick={close} disabled={busy} data-testid="report-cancel" className={secondaryButtonClass}>
              {t("Vazgeç", "Cancel")}
            </button>
            <button type="submit" disabled={!category || busy} data-testid="report-submit" className={primaryButtonClass}>
              {busy && <Loader2 size={14} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {t("Bildir", "Report")}
            </button>
          </div>
        </form>
      </PanelBody>
    </Panel>
  );
}
